import { useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import dayjs from 'dayjs';
import { getPresetRange } from '@prerender/design-system/DateRangePicker';

import { readDomainParam } from '~components/Filters/domainParam';
import { DISCOVERABILITY_DEFAULT_PRESET } from '../DiscoverabilityQueryParameters';
import { crawlerIdsInLineup } from '../DiscoverabilityCrawlers';
import { useAiCrawlerLineup } from '../../shared';

const DATE_PARAM_FORMAT = 'YYYY-MM-DD';

export type DiscoverabilitySearchParams = {
  dateFrom: string;
  dateTo: string;
  crawlers: string[] | undefined;
  domain: string | undefined;
};

// Splits a comma-separated URL param (e.g. `crawlers`, `domains`) into a
// trimmed list, dropping empty entries. Exported so the scan-scope hook can
// parse `domains` with the exact same rules.
export function parseCommaSeparatedParam(value: string): string[] {
  return value
    .split(',')
    .map((entry) => entry.trim())
    .filter((entry) => entry.length > 0);
}

/**
 * Reads the discoverability filter URL parameters (`dateFrom`, `dateTo`,
 * `crawlers`, `domain`) and falls back to the default date preset window when
 * either `dateFrom` or `dateTo` is absent or unparseable. The default range is captured once
 * on mount so the values stay stable across re-renders — recomputing it every
 * render would change the dates each tick and trigger an endless refetch loop
 * in the queries that consume them.
 */
export function useDiscoverabilitySearchParams(): DiscoverabilitySearchParams {
  const [searchParams] = useSearchParams();
  const lineup = useAiCrawlerLineup();

  const [defaultDates] = useState(() => {
    const { from, end } = getPresetRange(DISCOVERABILITY_DEFAULT_PRESET);
    return {
      dateFrom: from.format(DATE_PARAM_FORMAT),
      dateTo: end.format(DATE_PARAM_FORMAT),
    };
  });

  const urlDateFrom = searchParams.get('dateFrom');
  const urlDateTo = searchParams.get('dateTo');
  const urlCrawlers = searchParams.get('crawlers');
  // Both bounds must be present AND parseable. An unparseable date (e.g. a
  // hand-crafted `?dateFrom=notadate`) would otherwise flow through verbatim and
  // crash a consumer that turns it into an instant (PagesToReviewSection ->
  // resolveDiscoverabilityPagesScope -> Date.toISOString on an Invalid Date).
  const hasUrlDateRange =
    urlDateFrom !== null &&
    urlDateTo !== null &&
    dayjs(urlDateFrom).isValid() &&
    dayjs(urlDateTo).isValid();

  return {
    dateFrom: hasUrlDateRange ? urlDateFrom : defaultDates.dateFrom,
    dateTo: hasUrlDateRange ? urlDateTo : defaultDates.dateTo,
    crawlers:
      urlCrawlers !== null ? crawlerIdsInLineup(lineup, parseCommaSeparatedParam(urlCrawlers)) : undefined,
    domain: readDomainParam(searchParams.get('domain')),
  };
}
